import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LoadingButton } from './LoadingButton';
import PasswordInput from './PasswordInput';
import { tokenStorage } from '../utils/tokenStorage';
import type { LoginCredentials } from '../types/auth';
import './LoginForm.css';

interface LoginFormProps {
  onSuccess?: () => void;
  onForgotPassword?: () => void;
  className?: string;
}

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onForgotPassword,
  className = '',
}) => {
  const { login, isLoading, error, clearError } = useAuth();

  // Form state
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
  });

  const [rememberMe, setRememberMe] = useState(() =>
    tokenStorage.getRememberMe(),
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Clear auth error when component mounts or form data changes
  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [formData, clearError]);

  // Email validation
  const validateEmail = (email: string): string | undefined => {
    if (!email) {
      return 'Email is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }

    return undefined;
  };

  // Password validation
  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return 'Password is required';
    }

    if (password.length < 6) {
      return 'Password must be at least 6 characters';
    }

    return undefined;
  };

  // Real-time validation
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'email':
        return validateEmail(value);
      case 'password':
        return validatePassword(value);
      default:
        return undefined;
    }
  };

  // Handle input change with real-time validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear general error when user starts typing
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }

    // Real-time validation for touched fields
    if (touched[name]) {
      const fieldError = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: fieldError,
      }));
    }
  };

  // Handle field blur (mark as touched and validate)
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setTouched((prev) => ({ ...prev, [name]: true }));

    const fieldError = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError,
    }));
  };

  // Validate entire form
  const validateForm = (): boolean => {
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);

    const newErrors: FormErrors = {
      email: emailError,
      password: passwordError,
    };

    setErrors(newErrors);
    setTouched({ email: true, password: true });

    return !emailError && !passwordError;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await login(formData, rememberMe);
      onSuccess?.();
    } catch (err) {
      // Error is handled by the auth context
      console.error('Login failed:', err);
    }
  };

  // Handle forgot password click
  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    onForgotPassword?.();
  };

  // Check if form has errors
  const hasErrors = Object.values(errors).some((error) => error !== undefined);
  const isFormValid = formData.email && formData.password && !hasErrors;

  return (
    <div className={`login-form ${className}`}>
      <form onSubmit={handleSubmit} noValidate role="form">
        <div className="form-header">
          <h2>Sign In</h2>
          <p>Welcome back! Please sign in to your account.</p>
        </div>

        {/* General error message */}
        {(error || errors.general) && (
          <div className="error-message" role="alert">
            {error || errors.general}
          </div>
        )}

        {/* Email field */}
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className={`form-input ${errors.email ? 'error' : ''}`}
            placeholder="Enter your email"
            disabled={isLoading}
            autoComplete="email"
            aria-describedby={errors.email ? 'email-error' : undefined}
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <span id="email-error" className="field-error" role="alert">
              {errors.email}
            </span>
          )}
        </div>

        {/* Password field */}
        <div className="form-group">
          <label htmlFor="password" className="form-label">
            Password
          </label>
          <PasswordInput
            id="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="Enter your password"
            disabled={isLoading}
            autoComplete="current-password"
            hasError={!!errors.password}
            aria-describedby={errors.password ? 'password-error' : undefined}
            aria-invalid={!!errors.password}
          />
          {errors.password && (
            <span id="password-error" className="field-error" role="alert">
              {errors.password}
            </span>
          )}
        </div>

        {/* Remember me and forgot password */}
        <div className="form-options">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
            />
            <span className="checkbox-text">Remember me</span>
          </label>

          <button
            type="button"
            className="forgot-password-link"
            onClick={handleForgotPassword}
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </div>

        {/* Submit button */}
        <LoadingButton
          type="submit"
          loading={isLoading}
          loadingText="Signing in..."
          disabled={!isFormValid}
          className="submit-button loading-button--full-width"
          variant="primary"
          size="medium"
        >
          Sign In
        </LoadingButton>
      </form>
    </div>
  );
};

export default LoginForm;
